import { lazy, memo, Suspense } from 'react';
import { useAppSelector } from 'store/hooks';
import {
  selectRemoteScreensharingState,
  selectRemoteStreams,
} from 'features/streaming/streamingSlice';
import StreamMedia from 'features/streaming/components/stream-display/StreamMedia';
import { StreamingMediaProps } from 'features/streaming/types';
import RemoteVideoWrapper from 'features/streaming/components/media/remote-stream/RemoteVideoWrapper';
import { useStreamFitment } from 'hooks/useStreamFitment';
import { ZoomableContent } from 'features/content-zoom/components/ZoomableContent';
import { selectShowAllScreens } from 'features/screenshare/screenshareSlice';
import { useContentWhiteboard } from 'features/content-whiteboard/useContentWhiteboard';

const ScreenshareWhiteboardViewer = lazy(
  () => import('features/content-whiteboard/components/ScreenshareWhiteboardViewer')
);

const RemoteScreenshare = memo(({ source, avatarSize }: StreamingMediaProps) => {
  const fitment = useStreamFitment(source);

  const streams = useAppSelector((state) =>
    selectRemoteStreams(state, source.userId, source.feedId)
  );

  const isVideoBroadcasting = useAppSelector((state) =>
    selectRemoteScreensharingState(state, source.userId, 'video')
  );

  const showAllScreens = useAppSelector(selectShowAllScreens);

  const { isFeatureAvailable } = useContentWhiteboard();

  const renderContent = () => {
    const content = streams?.map((stream) =>
      stream.kind === 'video' && isVideoBroadcasting ? (
        <RemoteVideoWrapper
          mode="screenshare"
          key={stream.mid}
          mid={stream.mid}
          userId={source.userId}
          feedId={source.feedId}
          className={`fitment-${fitment}`}
        />
      ) : null
    );

    if (showAllScreens) {
      return content;
    }

    return (
      <ZoomableContent zoomId={source.feedId!}>
        {content}
        {isFeatureAvailable && source.feedId && (
          <Suspense fallback={null}>
            <ScreenshareWhiteboardViewer screenshareId={source.feedId} />
          </Suspense>
        )}
      </ZoomableContent>
    );
  };

  return (
    <StreamMedia source={source} avatarSize={avatarSize} hideAvatar>
      {renderContent()}
    </StreamMedia>
  );
});

export default RemoteScreenshare;
